import { useEffect, useState } from "react";

const MOBILE_QUERY = "(max-width: 767px)";
// Do not use the window "scroll" event here. Lighthouse and browser scroll
// restoration can emit it without a real user gesture and prematurely release
// every deferred image. A genuine mobile scroll starts with touch/pointer input.
const INTERACTION_EVENTS = ["pointerdown", "touchstart", "keydown"];
const subscribers = new Set();

let interactionDetected = false;
let listenersInstalled = false;

export function isJpegOrPng(url = "") {
  return /\.(?:jpe?g|png)(?:[?#]|$)/i.test(String(url));
}

export function isMobileViewport() {
  return typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches;
}

function removeInteractionListeners() {
  if (typeof window === "undefined" || !listenersInstalled) return;

  INTERACTION_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, notifyInteraction);
  });
  listenersInstalled = false;
}

function notifyInteraction() {
  if (interactionDetected) return;

  interactionDetected = true;
  removeInteractionListeners();
  subscribers.forEach((subscriber) => subscriber());
  subscribers.clear();
}

function installInteractionListeners() {
  if (
    typeof window === "undefined" ||
    listenersInstalled ||
    interactionDetected ||
    !isMobileViewport()
  ) {
    return;
  }

  INTERACTION_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, notifyInteraction, {
      once: true,
      passive: true,
    });
  });
  listenersInstalled = true;
}

export function subscribeToMobileImageInteraction(callback) {
  if (!isMobileViewport() || interactionDetected) {
    callback();
    return () => {};
  }

  subscribers.add(callback);
  installInteractionListeners();

  return () => subscribers.delete(callback);
}

export function shouldDeferMobileImage(url, allowAnyFormat = false) {
  return (
    isMobileViewport() &&
    !interactionDetected &&
    (allowAnyFormat || isJpegOrPng(url))
  );
}

export function useDeferredMobileImageSource(
  src,
  enabled = true,
  allowAnyFormat = false
) {
  const shouldDefer = enabled && shouldDeferMobileImage(src, allowAnyFormat);
  const [ready, setReady] = useState(!shouldDefer);

  useEffect(() => {
    if (!enabled || !shouldDeferMobileImage(src, allowAnyFormat)) {
      setReady(true);
      return undefined;
    }

    setReady(false);
    return subscribeToMobileImageInteraction(() => setReady(true));
  }, [allowAnyFormat, enabled, src]);

  return {
    src: ready ? src : undefined,
    deferred: !ready,
  };
}

export function deferMobileJpegPngInHtml(html = "") {
  if (
    !html ||
    typeof html !== "string" ||
    !isMobileViewport() ||
    interactionDetected
  ) {
    return html;
  }

  return html.replace(/<img\b[^>]*>/gi, (imageTag) => {
    const srcMatch = imageTag.match(/\ssrc\s*=\s*(["'])(.*?)\1/i);
    const src = srcMatch?.[2] || "";

    if (!isJpegOrPng(src) || /\sdata-mobile-src\s*=/i.test(imageTag)) {
      return imageTag;
    }

    let optimizedTag = imageTag
      .replace(srcMatch[0], ` data-mobile-src=${srcMatch[1]}${src}${srcMatch[1]}`)
      .replace(
        /\ssrcset\s*=\s*(["'])(.*?)\1/i,
        (_match, quote, srcset) => ` data-mobile-srcset=${quote}${srcset}${quote}`
      );

    if (!/\sloading\s*=/i.test(optimizedTag)) {
      optimizedTag = optimizedTag.replace(/^<img/i, '<img loading="lazy"');
    }
    if (!/\sdecoding\s*=/i.test(optimizedTag)) {
      optimizedTag = optimizedTag.replace(/^<img/i, '<img decoding="async"');
    }

    return optimizedTag.replace(/^<img/i, '<img data-mobile-deferred-image="true"');
  });
}

export function hydrateDeferredHtmlImages(root) {
  if (!root) return;

  root.querySelectorAll("img[data-mobile-src]").forEach((image) => {
    const srcset = image.getAttribute("data-mobile-srcset");
    const src = image.getAttribute("data-mobile-src");

    if (srcset) image.setAttribute("srcset", srcset);
    if (src) image.setAttribute("src", src);

    image.removeAttribute("data-mobile-srcset");
    image.removeAttribute("data-mobile-src");
    image.removeAttribute("data-mobile-deferred-image");
  });
}

export function useDeferredHtmlImages(containerRef, dependency) {
  useEffect(() => {
    const hydrate = () => hydrateDeferredHtmlImages(containerRef.current);
    const unsubscribe = subscribeToMobileImageInteraction(hydrate);

    if (!shouldDeferMobileImage("placeholder.jpg")) hydrate();
    return unsubscribe;
  }, [containerRef, dependency]);
}
